/**
 * memoUtils.ts
 *
 * This utility file provides React memoization helpers for components, including
 * specialized support for generic components where React.memo would otherwise
 * erase type parameters.
 */
import { memo } from "react";

/**
 * Wrapper around React.memo to respect generics.
 */
export const genericMemo: <Component>(component: Component) => Component = memo;

/**
 * Wraps a generic function component with `React.memo` **while preserving its generic type signature**.
 *
 * Tip:                 Use `typedMemo` anytime you're memoizing a component with generics, such as: `<T>`, `<TPresetType extends PresetType>`, dtc
 * Why you need this:   React.memo erases generic type parameters when used directly on a generic component. And doing an `InnerComponent` each time is a pain.
 *
 * Correct usage:
 * ```
 *      const Comp = typedMemo(function Comp<T>({ value }: { value: T }) {
 *         return <div>{value}</div>;
 *      });
 *
 *      <Comp<string> value="hello" />              // Works!
 * ```
 *
 * What DOES NOT WORK:
 * ```
 *      const Comp = memo(function Comp<T>({ value }: { value: T }) {
 *        return <div>{value}</div>;
 *      });
 *
 *      Comp<string> value="hello" />               // Error: Expected 0 type arguments
 * ```
 */
export function typedMemo<T extends (...args: any[]) => any>(component: T): T {
  // oxlint-disable-next-line endla/no-double-type-assertion -- Existing code
  return memo(component) as unknown as T;
}
